import { Button } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TachoTabs } from './TachographPopup';
import { ctxTachograph } from '@/contexts/TachographCtx';
import { FileDownload } from '@mui/icons-material';
import axios from 'axios';
import { PlutoClientException } from '@/PlutoClientException';
import { useNotificationStore } from '@/stores/notificationstore';

import { reportMessage } from '@/integrations/sentry/errorReporting';

export default function TachographGenerateButton({ activeTab }: { activeTab: TachoTabs }) {
  const { t } = useTranslation();
  const tachographCtx = useContext(ctxTachograph);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const options = [t('tachograph.downloadButton.downloadGeneralFile'), t('tachograph.downloadButton.downloadHrFile')];

  function downloadUrl() {
    if (
      !tachographCtx ||
      !tachographCtx.selectedDates ||
      !tachographCtx.selectedDates.from ||
      !tachographCtx.selectedDates.to ||
      (tachographCtx.selectedRowIds.length == 0 && tachographCtx.selectedRowCardNumbers.length == 0)
    ) {
      return undefined;
    }

    const params = new URLSearchParams();

    if (activeTab === 'TargetTab') {
      tachographCtx.selectedRowIds.forEach((id) => params.append('id', id.toString()));
      params.append('isCroatianFileName', tachographCtx.isCroatian.toString());
      params.append('fromTimeMillis', tachographCtx.selectedDates.from.getTime().toString());
      params.append('toTimeMillis', tachographCtx.selectedDates.to.getTime().toString());

      if (tachographCtx.selectedRowIds.length > 1) {
        return `/api/v1.1/tachograph/downloadMultipleVehicleData?${params.toString()}`;
      }

      return `/api/v1.1/tachograph/downloadSingleVehicleData?${params.toString()}`;
    } else {
      tachographCtx.selectedRowCardNumbers.forEach((driverCardNumber) => {
        const onlyCardNumber = driverCardNumber.toString().split('_')[1];
        params.append('driverCardNumbers', onlyCardNumber);
      });
      params.append('isCroatianFileName', tachographCtx.isCroatian.toString());
      params.append('fromTimeMillis', tachographCtx.selectedDates.from.getTime().toString());

      if (tachographCtx.selectedRowCardNumbers.length > 1) {
        return `/api/v1.1/tachograph/downloadMultipleDriverData?${params.toString()}`;
      }

      return `/api/v1.1/tachograph/downloadSingleDriverData?${params.toString()}`;
    }
  }

  const url = downloadUrl();

  const onDownloadClick = async () => {
    try {
      const response = await axios({
        method: 'get',
        url,
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];

      if (!contentDisposition) {
        reportMessage('Content-Disposition header is missing', 'warning');
      }

      const filename = decodeURIComponent(contentDisposition.split("filename*=UTF-8''")[1] ?? '');

      const blob = new Blob([response.data], { type: 'application/octet-stream' });

      const anchor = document.createElement('a');
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(anchor.href);
    } catch (error) {
      if (!(error instanceof PlutoClientException)) {
        return showNotification('error', t('error.api.title'), () => {}, 5);
      }

      switch (error.statusCode) {
        case 403:
          return showNotification('error', t('tachograph.error_forbidden'), () => {}, 5);
      }
    }
  };
  return (
    <>
      <Button
        onClick={onDownloadClick}
        disabled={!url}
        variant="contained"
        color={activeTab === 'DriverTab' ? 'secondary' : 'primary'}
        className="tachograph-generate-button"
        data-qa-element-id="download_file_button"
      >
        <FileDownload sx={{ marginRight: '10px' }} />
        {options[tachographCtx?.isCroatian ? 1 : 0]}
      </Button>
    </>
  );
}
